import { derived, type Readable } from 'svelte/store';
import { useUserContext } from '../user';
import { AuthStatus, useAuthContext } from '$lib/auth';
import { useFleetDashboardClient } from '../client';
import { createQueryOptions } from '../query-options';
import { locationKeys } from './query-key';
import { assertExists } from '$lib/assert';
import { createQuery } from '$lib/api/tanstack-query';

import type * as APITypes from '@viamrobotics/web-client/app/v1/app';
import type * as FleetDashboardTypes from '@viamrobotics/web-client/fleetdashboard/v1/fleetdashboard';
import { buildLocationMembers } from './build-members-and-invites';

/** Type to separate the current app user member from all other location members */
export interface LocationMembers {
  current: APITypes.OrganizationMember;
  others: APITypes.OrganizationMember[];
}

/**
 * List the members of a location.
 *
 * @param locationID the location ID
 * @returns a list of organization members
 */
export const useLocationMembers = (
  locationID: string | undefined
): Readable<LocationMembers | undefined> => {
  const listLocMembersQuery = createListLocMembersAndInvitesQuery(locationID);
  const { userID } = useUserContext();

  return derived([listLocMembersQuery, userID], ([$query, $userID]) =>
    buildLocationMembers($query, $userID)
  );
};

/**
 * List the invites in a location.
 *
 * @param locationID the location ID
 * @returns a list of location invites
 */
export const useLocationInvites = (
  locationID: string | undefined
): Readable<APITypes.OrganizationInvite[] | undefined> => {
  const listLocMembersQuery = createListLocMembersAndInvitesQuery(locationID);

  return derived(listLocMembersQuery, ($query) => $query.data?.invites);
};

/** Data from an RPC API call to ListLocationMembers. */
export interface ListLocationMembersQuery {
  data: FleetDashboardTypes.ListLocationMembersResponse | undefined;
}

const createListLocMembersAndInvitesQuery = (
  locationID: string | undefined
) => {
  const { authState, getRPCOptions } = useAuthContext();
  const fleetDashboardClient = useFleetDashboardClient();

  const queryOptions = derived([authState], ([$authState]) =>
    createQueryOptions({
      queryKey: locationKeys.membersAndInvites(locationID),
      enabled:
        $authState.status === AuthStatus.LoggedIn && locationID !== undefined,
      queryFn: async () => {
        assertExists(locationID, 'Missing location ID');

        const rpcOptions = await getRPCOptions();
        const { response } = await fleetDashboardClient.listLocationMembers(
          {
            locationId: locationID,
          },
          rpcOptions
        );

        return response;
      },
    })
  );

  return createQuery(queryOptions);
};
